/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import classnames from 'classnames';
import Search from '@mui/icons-material/Search';

import { Select } from 'uikit/selects';
import { TextInput } from 'uikit/inputs';
import { useOnClickOutside } from 'uikit/utils';

import CategoryNavigatorFilters from './categoryNavigatorFilters';
import {
  DropdownIndicator,
  NavigatorOption as Option,
  NavigatorSingleValue as SingleValue,
  NavigatorPlaceholder as Placeholder,
} from './categoryNavigatorSelectComponents';

import styles from '../styles/categoryNavigator.module.scss';

const CategoryNavigator = ({
  className = '',
  filters = [],
  filtersClassName = '',
  hasCustomFilterButton = false,
  isCustomFilterButtonDisabled = false,
  isDisabled = false,
  onCategoryNavigatorBlur = () => {},
  onClearFiltersClick = () => {},
  onClearSearchClick = () => {},
  onCustomFilterClick = () => {},
  onFilterClick = () => {},
  onSearchInputChange = () => {},
  onSearchInputFocus = () => {},
  onSelectChange = () => {},
  searchPlaceholder = i18n.t('search'),
  searchValue = '',
  selectedFilterValues = {},
  selectOptions = [],
  selectPlaceholder = i18n.t('select-an-option'),
  selectValue = null,
  shouldAnimateFilters = true,
  shouldShowClearFilters = false,
  shouldShowFilters = true,
  shouldShowSelect = true,
  testId = 'category-navigator',
}) => {
  const categoryNavigatorRef = useRef();

  const classNames = {
    control: ({ isFocused, menuIsOpen }) =>
      classnames(styles.control, {
        [styles.isFocused]: isFocused,
        [styles.menuIsOpen]: menuIsOpen,
      }),
    dropdownIndicator: () => styles.dropdownIndicator,
    menu: () => styles.menu,
    option: ({ isFocused, isSelected }) =>
      classnames(styles.option, {
        [styles.isFocused]: isFocused,
        [styles.isSelected]: isSelected,
      }),
    valueContainer: () => styles.valueContainer,
  };

  useOnClickOutside({ ref: categoryNavigatorRef, callback: onCategoryNavigatorBlur });

  return (
    <div
      data-testid={testId}
      ref={categoryNavigatorRef}
      className={classnames(styles.element, className)}
    >
      <div className={classnames(styles.search, isDisabled && styles.isDisabled)}>
        {shouldShowSelect && (
          <Select
            id="category-navigator"
            isDisabled={isDisabled}
            className={styles.select}
            classNames={classNames}
            placeholder={selectPlaceholder}
            options={selectOptions}
            onChange={onSelectChange}
            components={{
              DropdownIndicator,
              Option,
              Placeholder,
              SingleValue,
              IndicatorSeparator: () => null,
            }}
            value={selectValue}
            isSearchable={false}
          />
        )}
        <TextInput
          className={styles.searchInput}
          icon={<Search />}
          id="select-search-input"
          inputTestId="category-navigator-input"
          isDisabled={isDisabled}
          placeholder={searchPlaceholder}
          value={searchValue}
          onFocus={onSearchInputFocus}
          onChange={onSearchInputChange}
          shouldShowCloseButton={!!searchValue}
          onClick={onClearSearchClick}
        />
      </div>
      <CategoryNavigatorFilters
        filtersClassName={filtersClassName}
        hasCustomFilterButton={hasCustomFilterButton}
        isCustomFilterButtonDisabled={isCustomFilterButtonDisabled}
        isDisabled={isDisabled}
        onCustomFilterClick={onCustomFilterClick}
        shouldAnimateFilters={shouldAnimateFilters}
        shouldShowFilters={shouldShowFilters}
        onFilterClick={onFilterClick}
        onClearFiltersClick={onClearFiltersClick}
        filters={filters}
        selectedFilterValues={selectedFilterValues}
        shouldShowClearFilters={shouldShowClearFilters}
      />
    </div>
  );
};

const selectOptionPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.any,
  label: PropTypes.string,
});

CategoryNavigator.propTypes = {
  className: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      placeholder: PropTypes.string,
      options: PropTypes.arrayOf(selectOptionPropType),
      value: PropTypes.oneOfType([
        selectOptionPropType,
        PropTypes.arrayOf(selectOptionPropType),
        PropTypes.number,
        PropTypes.bool,
      ]),
    }),
  ),
  filtersClassName: PropTypes.string,
  hasCustomFilterButton: PropTypes.bool,
  isCustomFilterButtonDisabled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onCategoryNavigatorBlur: PropTypes.func,
  onClearFiltersClick: PropTypes.func,
  onClearSearchClick: PropTypes.func,
  onCustomFilterClick: PropTypes.func,
  onFilterClick: PropTypes.func,
  onSearchInputChange: PropTypes.func,
  onSearchInputFocus: PropTypes.func,
  onSelectChange: PropTypes.func,
  selectedFilterValues: PropTypes.shape({}),
  selectOptions: PropTypes.arrayOf(selectOptionPropType),
  selectPlaceholder: PropTypes.string,
  selectValue: selectOptionPropType,
  searchPlaceholder: PropTypes.string,
  searchValue: PropTypes.string,
  shouldAnimateFilters: PropTypes.bool,
  shouldShowClearFilters: PropTypes.bool,
  shouldShowFilters: PropTypes.bool,
  shouldShowSelect: PropTypes.bool,
  testId: PropTypes.string,
};

export default React.memo(CategoryNavigator);
